import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <header className="border-b border-stone-100 sticky top-0 bg-white/80 backdrop-blur-xl z-50">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] rounded-lg flex items-center justify-center">
              <FileText className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-lg font-extrabold tracking-tight text-stone-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Facturcal</span>
          </Link>
          <Link to="/" className="text-sm text-stone-500 hover:text-stone-800 flex items-center gap-1"><ArrowLeft className="w-4 h-4" />Volver</Link>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-extrabold tracking-tight text-stone-950 mb-8" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Política de Privacidad</h1>
        <div className="prose prose-stone max-w-none text-sm leading-relaxed space-y-6">
          <p className="text-stone-500">Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">1. Responsable del tratamiento</h2>
            <p className="text-stone-600">El responsable del tratamiento de los datos personales recogidos a través de <strong>facturcal.com</strong> es el titular de la plataforma Facturcal (en adelante, "el Responsable").</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">2. Datos que recopilamos</h2>
            <p className="text-stone-600">Recopilamos los siguientes datos personales:</p>
            <ul className="list-disc pl-6 text-stone-600 space-y-1 mt-2">
              <li>Datos de registro: nombre, email, contraseña (encriptada)</li>
              <li>Datos de facturación: NIF/CIF, dirección fiscal, datos bancarios</li>
              <li>Datos de clientes y proveedores que el usuario introduce</li>
              <li>Imágenes de facturas/tickets subidas para OCR</li>
              <li>Datos de uso: páginas visitadas, acciones realizadas</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">3. Finalidad del tratamiento</h2>
            <ul className="list-disc pl-6 text-stone-600 space-y-1">
              <li>Prestación del servicio de facturación electrónica</li>
              <li>Generación de documentos (facturas, presupuestos, albaranes, contratos)</li>
              <li>Procesamiento OCR de imágenes para extracción de datos</li>
              <li>Conciliación bancaria con datos proporcionados por el usuario</li>
              <li>Comunicaciones relacionadas con el servicio</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">4. Base legal</h2>
            <p className="text-stone-600">El tratamiento se basa en el consentimiento del usuario al registrarse y en la ejecución del contrato de prestación de servicios. Para el cumplimiento de obligaciones fiscales (Verifactu), la base legal es el cumplimiento de una obligación legal.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">5. Conservación de datos</h2>
            <p className="text-stone-600">Los datos se conservan mientras la cuenta esté activa. Los datos de facturación se conservan durante el plazo legal exigido por la normativa fiscal española (mínimo 4 años según Ley General Tributaria). El usuario puede solicitar la eliminación de su cuenta y datos no sujetos a obligación legal.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">6. Seguridad</h2>
            <p className="text-stone-600">Implementamos medidas técnicas y organizativas para proteger los datos:</p>
            <ul className="list-disc pl-6 text-stone-600 space-y-1 mt-2">
              <li>Comunicaciones cifradas mediante HTTPS/TLS</li>
              <li>Contraseñas encriptadas con bcrypt</li>
              <li>Autenticación mediante tokens JWT</li>
              <li>Acceso restringido por roles (RBAC)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">7. Derechos del usuario</h2>
            <p className="text-stone-600">El usuario puede ejercer los siguientes derechos conforme al RGPD:</p>
            <ul className="list-disc pl-6 text-stone-600 space-y-1 mt-2">
              <li><strong>Acceso:</strong> solicitar copia de sus datos personales</li>
              <li><strong>Rectificación:</strong> corregir datos inexactos</li>
              <li><strong>Supresión:</strong> solicitar la eliminación de sus datos</li>
              <li><strong>Portabilidad:</strong> recibir sus datos en formato estructurado</li>
              <li><strong>Oposición:</strong> oponerse al tratamiento</li>
              <li><strong>Limitación:</strong> solicitar la limitación del tratamiento</li>
            </ul>
            <p className="text-stone-600 mt-2">Para ejercer estos derechos, contacte con: <a href="mailto:info@facturcal.com" className="text-[#FF6B4A] hover:underline">info@facturcal.com</a></p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">8. Cookies</h2>
            <p className="text-stone-600">Facturcal utiliza cookies técnicas necesarias para el funcionamiento del servicio (autenticación, preferencias). No utilizamos cookies de seguimiento ni publicidad de terceros.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">9. Servicios de terceros</h2>
            <p className="text-stone-600">Utilizamos los siguientes servicios de terceros para la prestación del servicio:</p>
            <ul className="list-disc pl-6 text-stone-600 space-y-1 mt-2">
              <li>OpenAI: procesamiento de lenguaje natural y OCR (los datos se envían encriptados)</li>
              <li>Proveedor de hosting en la nube (servidores en la UE)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-stone-900 mt-8 mb-3">10. Contacto</h2>
            <p className="text-stone-600">Para cualquier consulta sobre esta política de privacidad, puede contactarnos en: <a href="mailto:info@facturcal.com" className="text-[#FF6B4A] hover:underline">info@facturcal.com</a></p>
          </section>
        </div>
      </main>
      <footer className="border-t border-stone-100 py-8 text-center text-xs text-stone-400">
        © {new Date().getFullYear()} Facturcal. Todos los derechos reservados.
      </footer>
    </div>
  );
};

export default LegalPage;
